import { useInfiniteQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import SORT_OPTIONS from '@/shared/constants/centersSortOptions';
import { queryKeys } from '@/shared/constants/queryKeys';

import { getAllBowlingCenters } from '@/services/home';

const BC_FIELDS = [
  'uuid',
  'lanes',
  'activeGames',
  'address',
  'companyName',
  'country',
  'countryCode',
  'logoUrl',
  'viewersCurrent',
  'features',
];

const PAGE_LIMIT = 50;

// Fetches and manages bowling centers data
function useBowlingCentersData({ location }) {
  const [searchParams] = useSearchParams();
  const search = searchParams.get('q');
  const orderCriteria = {
    order: searchParams.get('order') || SORT_OPTIONS.GAMES,
    sort: searchParams.get('sort') || 'desc',
  };

  const {
    data,
    isPending,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isError,
    error,
  } = useInfiniteQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [
      queryKeys.allBowlingCenters,
      orderCriteria.order,
      orderCriteria.sort,
      search,
      ...(orderCriteria.order === SORT_OPTIONS.PROXIMITY
        ? `${location?.latitude},${location?.longitude}`
        : ``),
    ],
    queryFn: ({ pageParam }) =>
      getAllBowlingCenters({
        search: search !== '' ? search : undefined,
        fields: BC_FIELDS.join(','),
        page: pageParam,
        limit: PAGE_LIMIT,
        // includeManual: 0,
        ...(orderCriteria.order === SORT_OPTIONS.PROXIMITY
          ? {
              latitude: location?.latitude,
              longitude: location?.longitude,
              sortBy: 'location',
              sortDirection: 'asc',
            }
          : {
              sortBy: orderCriteria.order,
              sortDirection: orderCriteria.sort,
            }),
      }),
    select: ({ pages }) =>
      pages
        .map(({ data }) =>
          data.map(({ countryCode, ...bc }) => ({
            ...bc,
            countryFlag: `https://flagcdn.com/w20/${countryCode}.jpg`,
          }))
        )
        .flat(),
    enabled:
      search !== '' ||
      orderCriteria.order !== SORT_OPTIONS.PROXIMITY ||
      !!location,
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      if (lastPage?.data?.length < 50) {
        return undefined;
      }
      return lastPageParam + 1;
    },
  });

  return {
    bowlingCenters: {
      data,
      isLoading: isPending,
      onLoadMore: fetchNextPage,
      hasMore: hasNextPage,
      isFetchingNextPage,
      isError,
      error,
    },
  };
}

export default useBowlingCentersData;
